import { operationIds } from "@gen/app-access-config";
import { Query } from "@gen/app-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  useCanGoBack,
  useNavigate,
  useParams,
  useRouter,
} from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { LoadingScreen } from "~/components/loading_screen";
import { useCanExecuteOperation } from "~/contexts/auth_query";

import { KycFooter } from "./KycFooter";
import { KycOnboarding } from "./KycOnboarding";
import { KycSidePanel } from "./KycSidePanel";
import { KycSubmitted } from "./KycSubmitted";

const SUBMITTED_REDIRECT_DELAY_MS = 3000;

export function Kyc() {
  const canExecuteOperation = useCanExecuteOperation();
  const router = useRouter();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const clientId = useParams({
    from: "/clients/$clientId/kyc",
    select(params) {
      return params.clientId;
    },
  });

  const backToDashboard = useCallback(() => {
    if (canGoBack) {
      router.history.back();
      return;
    }

    void navigate({
      to: "/clients/$clientId/transactions",
      params: { clientId },
    });
  }, [canGoBack, clientId, navigate, router]);

  useEffect(() => {
    if (!isSubmitted) {
      return;
    }

    const timeout = setTimeout(backToDashboard, SUBMITTED_REDIRECT_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [isSubmitted, backToDashboard]);

  const { data: clientData, isFetching: isClientFetching } =
    Query.useCore_getClientQuery(clientId);

  const publishableKey = clientData?.stripe_publishable_key;
  const canStartOnboarding = canExecuteOperation(
    operationIds["core.getClientOnboardingSession"],
  );

  function renderContent() {
    if (isSubmitted) {
      return <KycSubmitted onBack={backToDashboard} />;
    }

    if (isClientFetching && !publishableKey) {
      return <LoadingScreen />;
    }

    if (!canStartOnboarding || !publishableKey) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
          <Typography variant="body1" data-testid="kyc-unavailable-text">
            Account verification is not available for this client.
          </Typography>
        </Stack>
      );
    }

    return (
      <KycOnboarding
        clientId={clientId}
        publishableKey={publishableKey}
        onExit={() => setIsSubmitted(true)}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        minHeight: "100vh",
        bgcolor: "gray.50",
      }}
    >
      <KycSidePanel onBack={backToDashboard} />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 7.5, lg: 5 },
          pb: { xs: 2.5, lg: 5 },
          px: { xs: 2.5, sm: 5 },
          pl: { lg: 7.5 },
          pr: { lg: 5, xl: 0 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 800, my: { lg: "auto" } }}>
          {renderContent()}
        </Box>
        <KycFooter />
      </Box>
    </Box>
  );
}
